import { FlexBox, Skeleton } from '@dixa/lib-shared-habla';
import { AnalyticsMenu, AnalyticsMenuActions } from 'lib-analytics-ui';
import { useHasAccess } from 'lib-shared-access-control';
import { ampli } from 'lib-shared-tracking';
import { memo, useMemo } from 'react';

import { AppLink } from 'components/appLink';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { DragMoveEvent } from 'utils/useOrderedDragDrop';

import { useQueueDashboardQueueContext } from '../../queueDashboardQueue.context';

type QueueDashboardQueueActionsProps = {
  /** Handler called when the order of the queues changes. */
  onOrderChange?: (event: DragMoveEvent) => void;
};

export const QueueDashboardQueueActions = memo(
  function QueueDashboardQueueActions({
    onOrderChange,
  }: QueueDashboardQueueActionsProps): JSX.Element {
    const { index, totalQueues, isSkeleton, ...context } =
      useQueueDashboardQueueContext();
    const queue = context.queue!;

    const flags = useFeatureFlag(['custom-analytics']);

    const hasAnalyticsAccess = useHasAccess('analytics');
    const hasQueueSettingsAccess = useHasAccess(
      ['settings.queue.offering', 'settings.queue.members'],
      {
        strategy: 'any',
      },
    );

    const orderingActions = useMemo((): AnalyticsMenuActions => {
      if (!onOrderChange) return [];

      const lastIndex = totalQueues - 1;

      return [
        {
          type: 'divider',
        },
        {
          type: 'button',
          icon: 'arrow-square-up',
          label: 'Move to top',
          disabled: index === 0,
          onClick: () => onOrderChange({ dragIndex: index, hoverIndex: 0 }),
        },

        {
          type: 'button',
          icon: 'arrow-up',
          label: 'Move up',
          disabled: index === 0,
          onClick: () =>
            onOrderChange({
              dragIndex: index,
              hoverIndex: Math.max(0, index - 1),
            }),
        },
        {
          type: 'button',
          icon: 'arrow-down',
          label: 'Move down',
          disabled: index === lastIndex,
          onClick: () =>
            onOrderChange({
              dragIndex: index,
              hoverIndex: index + 1,
            }),
        },
        {
          type: 'button',
          icon: 'arrow-square-down',
          label: 'Move to bottom',
          disabled: index === lastIndex,
          onClick: () =>
            onOrderChange({ dragIndex: index, hoverIndex: lastIndex }),
        },
      ];
    }, [index, onOrderChange, totalQueues]);

    const actions = useMemo(
      (): AnalyticsMenuActions => [
        {
          type: 'link',
          icon: 'conversations',
          label: 'See all conversations',
          link: (
            <AppLink
              path="/conversations"
              params={{
                search: {
                  queue: [queue.id],
                },
              }}
            />
          ),
          onClick: () => {
            ampli.dashboardQueueAllConversationsClicked();
          },
        },

        ...(flags.customAnalytics && hasAnalyticsAccess
          ? ([
              {
                type: 'link',
                icon: 'chart-bar',
                label: 'Go to Analytics',
                link: (
                  <AppLink
                    path="/analytics/dashboard/:slug"
                    params={{
                      path: { slug: 'queues' },
                      search: {
                        period: 'today',
                        context: `queue_id~is_one~${queue.id}`,
                      },
                    }}
                  />
                ),
                onClick: () => {
                  ampli.dashboardQueueGoToAnalyticsClicked();
                },
              },
            ] satisfies AnalyticsMenuActions)
          : []),

        {
          type: 'link',
          icon: 'presentation',
          label: 'TV mode',
          link: (
            <AppLink
              path="/tv/dashboard/:queueId"
              params={{
                path: {
                  queueId: queue.id,
                },
              }}
            />
          ),
          onClick: () => {
            ampli.dashboardQueueTvDashboardClicked();
          },
        },

        ...(hasQueueSettingsAccess
          ? ([
              {
                type: 'divider',
              },
              {
                type: 'link',
                icon: 'cog',
                label: 'Queue settings',
                link: (
                  <AppLink
                    path="/settings/queues/:id"
                    params={{ path: { id: queue.id } }}
                  />
                ),
                onClick: () => {
                  ampli.dashboardQueueSettingsClicked();
                },
              },
            ] satisfies AnalyticsMenuActions)
          : []),

        ...orderingActions,
      ],
      [
        hasAnalyticsAccess,
        hasQueueSettingsAccess,
        flags.customAnalytics,
        queue.id,
        orderingActions,
      ],
    );

    return (
      <>
        {isSkeleton ? (
          <FlexBox
            justifyContent="center"
            alignItems="center"
            width="32px"
            height="32px"
          >
            <Skeleton height="18px" width="6px" />
          </FlexBox>
        ) : (
          <AnalyticsMenu actions={actions} />
        )}
      </>
    );
  },
);
